<template>
  <div :id="`${source}_outbound_caller_section`" class="flex w-full">
    <SkeletonLoader v-if="loading" :page="loaderPages.OUTBOUND_SELECT" />

    <div v-else class="flex flex-col gap-1 w-full">
      <div class="flex items-center">
        <span class="secondary_color text-[13px] mr-1">Outbound Caller ID</span>
        <a-tooltip
          placement="top"
          class="cursor-pointer"
          title="Set the number shown on outbound calls. Once verified, caller ID works in most countries, but some countries might require separate verification."
        >
          <ThemeImage
            :icon="imageNames.INFO_GREY"
            alt="info_circle"
            class="size-4"
          />
        </a-tooltip>
      </div>

      <a-select
        v-model:value="selectedCallerIDID"
        class="w-full"
        placeholder="Select caller ID"
        :dropdown-match-select-width="false"
        :disabled="provider === telephonyProviders.plivo"
        @change="handleCallerIDChange"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW_GREY"
            alt="dropdown_arrow"
            class="size-4"
          />
        </template>

        <template #dropdownRender="{ menuNode: menu }">
          <v-nodes :source="source" :vnodes="menu" />

          <div
            class="select_vnode !gap-[2px] mt-1"
            @click="openAddCallerIDModal"
          >
            <ThemeImage
              class="size-[14px]"
              :icon="imageNames.PLUS"
              alt="add_icon"
            />

            <span class="text-sm font-normal">Add Caller ID</span>
          </div>
        </template>

        <a-select-option
          v-for="callerID in callerIDs"
          :key="callerID.id"
          :value="callerID.id"
        >
          <div class="flex items-center justify-between w-full">
            <span class="text_color">{{ callerID.phone_number }}</span>

            <ThemeImage
              v-if="callerID.id"
              @click.stop="deleteCallerID(callerID.id)"
              :icon="imageNames.DELETE_GREY"
              alt="delete"
              class="size-4 cursor-pointer"
            />
          </div>
        </a-select-option>
      </a-select>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, defineComponent } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";

const VNodes = defineComponent({
  props: {
    vnodes: {
      type: Object,
      required: true,
    },
  },
  render() {
    return this.vnodes;
  },
});

const props = defineProps({
  source: { type: String, required: true },
  formData: { type: Object, required: true },
  provider: { type: String, default: telephonyProviders.twilio },
});

const selectedCallerIDID = ref(props.formData.outboundCallerID || 0);
const callerIDs = ref([]);
const loading = ref(false);

const handleCallerIDChange = (value) => {
  if (value === 0) {
    props.formData.outboundCallerID = 0;
  } else {
    const callerID = callerIDs.value.find((id) => id.id === value);
    if (callerID) {
      props.formData.outboundCallerID = value;
    }
  }
};

const openAddCallerIDModal = () => {
  try {
    eventBus.emit("open-add-caller-id-modal", { provider: props.provider });
  } catch (error) {
    console.error(
      "An error occurred while opening add caller ID modal: ",
      error,
    );
  }
};

const handleCallerIDAdded = async (newCallerID) => {
  try {
    await toGetCallerIDs(false);
    selectedCallerIDID.value = newCallerID.id;
    props.formData.outboundCallerID = newCallerID.id;
  } catch (error) {
    console.error("An error occurred while handling caller ID added: ", error);
  }
};

const deleteCallerID = async (callerIDID) => {
  try {
    const response = await CallService.deleteCallerID(callerIDID);
    if (response?.success) {
      callerIDs.value = callerIDs.value.filter((id) => id.id !== callerIDID);
      if (selectedCallerIDID.value === callerIDID) {
        selectedCallerIDID.value = 0;
        props.formData.outboundCallerID = 0;
      }

      message.success("Successfully deleted the Caller ID!");
    }
  } catch (error) {
    console.error("An error occurred while deleting caller ID: ", error);
  }
};

const toGetCallerIDs = async (loader = true) => {
  try {
    if (loader) loading.value = true;

    const params = {
      provider: props.provider,
    };

    const response = await CallService.getCallerIDs(params);
    if (response?.success) {
      const data = response?.data;
      callerIDs.value = [
        { id: 0, phone_number: "Default" },
        ...data?.caller_ids,
      ];
    }
  } catch (error) {
    console.error("An error occurred while getting caller IDs: ", error);
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.formData.outboundCallerID,
  (newValue) => {
    selectedCallerIDID.value = newValue || 0;
  },
);

onMounted(async () => {
  await toGetCallerIDs();
  eventBus.on("caller-id-added", handleCallerIDAdded);
});

onUnmounted(() => {
  eventBus.off("caller-id-added", handleCallerIDAdded);
});
</script>
